import { ScrollArea } from "@base-ui/react/scroll-area"
import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { OverlayStyle } from "shared/context/overlay/types"
import { useScreenSize } from "shared/lib/useScreenSize"
import { WithClassName } from "shared/types/WithClassName"
import { Button, ButtonProps, ButtonVariant } from "shared/ui/buttons/Button"

import { OverlayHeader } from "./OverlayHeader"
import { OverlayWrapper } from "./OverlayWrapper"

export interface HookFormOverlayProps extends WithClassName {
  id: string
  form: string
  prefix?: string
  headline: React.ReactNode
  children?: React.ReactNode | React.ReactNode[]

  submitButtonText?: string
  cancelButtonText?: string
  isSubmitDisabled?: boolean
  isCancelDisabled?: boolean
  isCancelHidden?: boolean
  submitButtonProps?: ButtonProps
  cancelButtonProps?: ButtonProps
  isLoading?: boolean
  isDisabled?: boolean

  hideControls?: boolean
  showCloseButton?: boolean

  onCancel?: () => void
  onSubmit?: () => void
}
export const FormOverlay = React.memo(
  ({
    id,
    form,
    headline,

    children,
    isCancelHidden,
    className,
    isSubmitDisabled,
    isCancelDisabled,
    cancelButtonText,
    submitButtonText,
    isLoading,
    isDisabled,
    prefix,
    hideControls,
    showCloseButton,
    submitButtonProps,
    cancelButtonProps,
    onCancel,
    onSubmit
  }: HookFormOverlayProps) => {
    const { t } = useTranslation()
    const { isTablet } = useScreenSize()

    return (
      <OverlayWrapper
        id={id}
        headline={headline}
        overlayStyle={OverlayStyle.FORM}
        className={className}
        mobileFullscreen
      >
        <OverlayHeader.Container prefix={prefix || id}>
          <OverlayHeader.CloseButton
            isHidden={(isCancelHidden || !isTablet) && !showCloseButton}
            onClick={onCancel}
            label={t("General.Cancel")}
            data-testid={`${prefix || "form"}__cancel`}
            {...cancelButtonProps}
            className={classNames("secondary", cancelButtonProps?.className)}
          />
          <OverlayHeader.Title>{headline}</OverlayHeader.Title>
          <OverlayHeader.Action
            type="submit"
            onClick={onSubmit}
            form={form}
            disabled={isSubmitDisabled}
            label={submitButtonText || t("General.Confirm")}
            data-testid={`${prefix || "form"}__submit`}
            {...submitButtonProps}
            className={classNames("primary", submitButtonProps?.className)}
          />
        </OverlayHeader.Container>

        <ScrollArea.Root className="scroll-area scroll-area--vertical shadow-s overlay-scroll-container">
          <ScrollArea.Viewport className="scroll-area__viewport">
            <ScrollArea.Content className="scroll-area__content">
              {children}
            </ScrollArea.Content>
          </ScrollArea.Viewport>
          <ScrollArea.Scrollbar
            orientation="vertical"
            className="scroll-area__scrollbar"
          >
            <ScrollArea.Thumb className="scroll-area__thumb" />
          </ScrollArea.Scrollbar>
        </ScrollArea.Root>

        {hideControls ? null : (
          <div className="controls form-overlay__controls">
            <Button
              variant={ButtonVariant.TERTIARY}
              data-testid={`${prefix || "form"}__cancel`}
              onClick={onCancel}
              label={cancelButtonText || t("General.Cancel")}
              isHidden={isCancelHidden}
              disabled={isDisabled || isCancelDisabled}
              {...cancelButtonProps}
            />
            <Button
              variant={ButtonVariant.PRIMARY}
              data-testid={`${prefix || "form"}__submit`}
              isLoading={isLoading}
              type="submit"
              form={form}
              disabled={isDisabled || isSubmitDisabled}
              {...submitButtonProps}
              label={submitButtonText || t("General.Confirm")}
            />
          </div>
        )}
      </OverlayWrapper>
    )
  }
)
